import BaseModel from "lib/model";
import BaseCollection from "lib/collection";
import Globalize from "globalize";
import me from "models/me";
import moment from "moment";
import titleCase from "lib/title-case";
import truncate from "truncate";

const FormatterModel = BaseModel.extend({
    props: {
        formatter: { type: "string", required: true },
        params: { type: "array" },
    },
});

const FormatterCollection = BaseCollection.extend({
    model: FormatterModel,
});

const CommonCollection = BaseCollection.extend({
    mainIndex: "_id",
    model: BaseModel,
});

const BaseElementModel = BaseModel.extend({
    idAttribute: "reference",
    props: {
        base: { type: "string", required: true },
        label: { type: "string" },
        editable: { type: "boolean", required: true, default: false },
        reference: { type: "string", required: true },
        type: { type: "string", required: true },
        default_value: { type: "string", required: true, default: "" },
    },

    collections: {
        formatters: FormatterCollection,
        derived_from: CommonCollection,
    },

    convertLegacyDateFormat(legacyFormat) {
        return legacyFormat
            .replace("%mon", "MMM")
            .replace("%mm", "M")
            .replace("%0mm", "MM")
            .replace("%dd", "D")
            .replace("%0dd", "DD")
            .replace("%yyyy", "YYYY");
    },

    formatValue(value, formatter) {
        switch (formatter.formatter) {
            case "money":
                // eslint-disable-next-line no-case-declarations
                const numericValue = Number(value);
                if (!Number.isNaN(numericValue)) {
                    value = Globalize.formatCurrency(numericValue, me._activeCurrency);
                }
                break;
            case "date":
                if (formatter.params && formatter.params.length) {
                    const format = this.convertLegacyDateFormat(formatter.params[0]);
                    value = moment.utc(value).format(format);
                }
                break;
            case "uppercase":
                value = value.toString().toUpperCase();
                break;
            case "lowercase":
                value = value.toString().toLowerCase();
                break;
            case "sentence_case":
                // eslint-disable-next-line no-useless-escape
                value = value
                    .toString()
                    .toLowerCase()
                    .replace(/(^\s*\w|[\.!\?]\s*\w)/g, function (c) {
                        return c.toUpperCase();
                    });
                break;
            case "capitalize_words":
                value = titleCase(value.toString());
                break;
            case "truncate":
                if (formatter.params && formatter.params.length) {
                    value = truncate(value.toString(), formatter.params[0]);
                }
                break;
            default:
                value = value.toString();
        }
        return value;
    },
    getValue(item, field) {
        let value;
        if (!field) {
            field = this;
        }
        if (field.type === "widget") {
            return ""; // Widgets are unsupported
        }
        if (field.derived_from.length) {
            value = field.derived_from.map((subfield) => {
                return subfield.getValue(item);
            });
        } else if (!field.reference) {
            value = field.default_value;
        } else {
            value = item[field.reference] || field.default_value;
        }

        if (value && !Array.isArray(value)) {
            field.formatters.forEach((formatter) => {
                value = this.formatValue(value, formatter);
            });
        } else if (value && Array.isArray(value)) {
            value.forEach((val, index) => {
                if (val) {
                    field.formatters.forEach((formatter) => {
                        val = this.formatValue(val, formatter);
                        value[index] = val;
                    });
                }
            });
        }

        if (field.type === "view") {
            if (field.reference === "concatenate") {
                if (Array.isArray(value)) {
                    value = value.join("");
                }
            }
        }

        if (Array.isArray(value)) {
            // used for seasons, etc
            value = value.join(", ");
        }

        return value;
    },
});

const BaseElementCollection = BaseCollection.extend({
    mainIndex: "_id",
    model: BaseElementModel,
});

const ElementModel = BaseElementModel.extend({
    collections: {
        derived_from: BaseElementCollection,
    },
});

const ElementCollection = BaseCollection.extend({
    mainIndex: "_id",
    model: ElementModel,
});

const SectionModel = BaseModel.extend({
    idAttribute: "key",
    props: {
        key: { type: "string", required: true },
    },
    collections: {
        elements: ElementCollection,
    },
});

const SectionCollection = BaseCollection.extend({
    mainIndex: "key",
    model: SectionModel,
});

export default BaseModel.extend({
    props: {
        active: { type: "boolean", required: true, default: false },
        view_name: { type: "string", required: true },
        schema_id: { type: "objectId" },
        key: { type: "string", required: true },
    },
    collections: {
        sections: SectionCollection,
    },

    urlRoot: function () {
        if (me.isGuestBuyer()) {
            return `/api/v3.0/view/${this.view_name}/schema/${this.schema_id}?viewAsGuestBuyer=true`;
        } else {
            return `/api/v3.0/view/${this.view_name}/schema/${this.schema_id}`;
        }
    },
});
